import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import Swal from "sweetalert2";
import ScaleLoader from "react-spinners/ScaleLoader";
import { createChat } from "../../api/api";
import logo from "../../assets/logo_project.png";
import styles from "./ChatByDescription.module.css";

interface ProductSuggestion  {
    code: string;
    descripcion: string;
    document_name: string;
    modelo: string;
}

interface SuggestionsResponse {
    suggestions: ProductSuggestion[];
}

const ChatByDescription = () => {
    const { description } = useParams<{ description: string }>();
    const navigate = useNavigate();

    const [selectedProduct, setSelectedProduct] = useState<ProductSuggestion | null>(null);
    const [isCreatingChat, setIsCreatingChat] = useState(false);
    const [currentDescription, setCurrentDescription] = useState(description || "");
    const [searchDescription, setSearchDescription] = useState(description || "");
    const [showSearchInput, setShowSearchInput] = useState(false);

    // API call to get suggestions based on description
    const fetchSuggestionsByDescription = async (desc: string): Promise<SuggestionsResponse> => {
        const response = await fetch(`/suggest?q=${encodeURIComponent(desc)}&top=20`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || "Error fetching product suggestions");
        }

        return await response.json();
    };

    // Error handler using async/await
    const handleQueryError = useCallback(
        async (error: unknown) => {
            console.error("Error fetching suggestions:", error);
            setShowSearchInput(true);

            try {
                const result = await Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "No se pudieron obtener sugerencias para la descripción proporcionada.",
                    confirmButtonText: "Intentar de nuevo",
                    showCancelButton: true,
                    cancelButtonText: "Volver",
                    confirmButtonColor: "#2f9f91",
                    cancelButtonColor: "#6c757d"
                });

                if (!result.isConfirmed) {
                    navigate("/ChatInit");
                }
            } catch (swalError) {
                console.error("Error showing alert:", swalError);
                navigate("/ChatInit");
            }
        },
        [navigate]
    );

    // React Query to fetch suggestions
    const {
        data: suggestionsData,
        isLoading: isSuggestionsLoading,
        error: suggestionsError,
        refetch: refetchSuggestions
    } = useQuery(["productSuggestions", searchDescription], () => fetchSuggestionsByDescription(searchDescription), {
        enabled: !!searchDescription,
        retry: 2,
        retryDelay: 1000,
        onError: handleQueryError
    });

    const handleProductSelect = (product: ProductSuggestion) => {
        setSelectedProduct(product);
    };

    // Create chat immediately when user clicks a suggestion
    const handleSelectAndCreate = async (product: ProductSuggestion) => {
        // prevent multiple triggers
        if (isCreatingChat) return;

        setSelectedProduct(product);
        setIsCreatingChat(true);

        try {
            // Store product information in session storage
            sessionStorage.setItem("CodigoProducto", product.code);
            sessionStorage.setItem("DescripcionProducto", searchDescription);
            sessionStorage.setItem("SearchType", "descripcion");
            sessionStorage.setItem("SelectedProduct", JSON.stringify(product));
            const userId = sessionStorage.getItem("userEmail") || "";
            const userName = sessionStorage.getItem("userName") || "";

            const response = await createChat(product.code, userId, userName);

            if (response && response.chat && response.chat.id) {
                navigate(`/chatv2/${response.chat.id}`);
            } else {
                throw new Error("Invalid response from chat creation");
            }
        } catch (error) {
            console.error("Error creating chat on select:", error);
            try {
                await Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "No se pudo crear el chat. Por favor, intenta de nuevo.",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "#2f9f91"
                });
            } catch (swalError) {
                console.error("Error showing error alert:", swalError);
            }
        } finally {
            setIsCreatingChat(false);
        }
    };

    const handleCreateChat = async () => {
        if (!selectedProduct) {
            try {
                await Swal.fire({
                    icon: "warning",
                    title: "Selección requerida",
                    text: "Por favor, selecciona un producto antes de continuar.",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "#2f9f91"
                });
            } catch (error) {
                console.error("Error showing warning:", error);
            }
            return;
        }

        setIsCreatingChat(true);

        try {
            // Store product information in session storage
            sessionStorage.setItem("CodigoProducto", selectedProduct.code);
            sessionStorage.setItem("DescripcionProducto", searchDescription);
            sessionStorage.setItem("SearchType", "descripcion");
            sessionStorage.setItem("SelectedProduct", JSON.stringify(selectedProduct));
            const userId = sessionStorage.getItem("userEmail") || "";
            const userName = sessionStorage.getItem("userName") || "";

            // Create chat with the selected product code
            const response = await createChat(selectedProduct.code, userId, userName);

            if (response && response.chat && response.chat.id) {
                console.log("Chat created successfully for product:", selectedProduct.code);
                // Navigate to the chat page
                navigate(`/chatv2/${response.chat.id}`);
            } else {
                throw new Error("Invalid response from chat creation");
            }
        } catch (error) {
            console.error("Error creating chat:", error);

            try {
                await Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "No se pudo crear el chat. Por favor, intenta de nuevo.",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "#2f9f91"
                });
            } catch (swalError) {
                console.error("Error showing error alert:", swalError);
            }
        } finally {
            setIsCreatingChat(false);
        }
    };

    const handleGoBack = () => {
        navigate("/ChatInit");
    };

    const handleNewSearch = async () => {
        const trimmedDescription = currentDescription.trim();

        if (!trimmedDescription) {
            try {
                await Swal.fire({
                    icon: "warning",
                    title: "Descripción requerida",
                    text: "Por favor, ingresa una descripción para buscar.",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "#2f9f91"
                });
            } catch (error) {
                console.error("Error showing warning:", error);
            }
            return;
        }

        // Clear previous selection
        setSelectedProduct(null);
        setSearchDescription(trimmedDescription);

        // Update URL to reflect new search
        navigate(`/search-description/${encodeURIComponent(trimmedDescription)}`, { replace: true });
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentDescription(event.target.value);
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleNewSearch();
        }
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
        if (!showSearchInput) {
            setCurrentDescription(searchDescription);
        }
    };

    const truncateText = (text: string, maxLength: number = 200) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    // Loading state
    if (isSuggestionsLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <img src={logo} alt="Logo" className={styles.logo} />
                    <h2>Buscando productos...</h2>
                    <p>
                        Descripción: <strong>{searchDescription}</strong>
                    </p>
                    <ScaleLoader color="#2f9f91" height={35} width={4} radius={2} margin={2} />
                </div>
            </div>
        );
    }

    // Error state
    if (suggestionsError && !showSearchInput) {
        return (
            <div className={styles.container}>
                <div className={styles.errorContainer}>
                    <img src={logo} alt="Logo" className={styles.logo} />
                    <h2>Error al buscar productos</h2>
                    <p>
                        No se pudieron obtener sugerencias para: <strong>{searchDescription}</strong>
                    </p>
                    <div className={styles.errorActions}>
                        <button onClick={toggleSearchInput} className={styles.retryButton}>
                            Cambiar búsqueda
                        </button>
                        <button onClick={handleGoBack} className={styles.backButton}>
                            Volver
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // No suggestions found
    if (suggestionsData?.suggestions?.length === 0 && !showSearchInput) {
        return (
            <div className={styles.container}>
                <div className={styles.noResultsContainer}>
                    <img src={logo} alt="Logo" className={styles.logo} />
                    <h2>No se encontraron productos</h2>
                    <p>
                        No hay productos que coincidan con: <strong>{searchDescription}</strong>
                    </p>
                    <div className={styles.errorActions}>
                        <button onClick={toggleSearchInput} className={styles.retryButton}>
                            Cambiar búsqueda
                        </button>
                        <button onClick={handleGoBack} className={styles.backButton}>
                            Volver
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <img src={logo} alt="Logo" className={styles.logo} />
                <h1>Selecciona un Producto</h1>
                <p className={styles.searchDescription}>
                    Resultados para: <strong>{searchDescription}</strong>
                </p>

                {/* Search modification section */}
                <div className={styles.searchModifySection}>
                    <button onClick={toggleSearchInput} className={styles.modifySearchButton}>
                        {showSearchInput ? "Cancelar" : "Cambiar búsqueda"}
                    </button>
                </div>

                {showSearchInput && (
                    <div className={styles.searchInputContainer}>
                        <div className={styles.searchInputWrapper}>
                            <input
                                type="text"
                                value={currentDescription}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                                placeholder="Ingresa una nueva descripción..."
                                className={styles.searchInput}
                                autoFocus
                            />
                            <div className={styles.searchInputActions}>
                                <button onClick={handleNewSearch} className={styles.searchButton} disabled={!currentDescription.trim()}>
                                    Buscar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Show suggestions only if we have them and search input is not showing */}
            {suggestionsData?.suggestions && suggestionsData.suggestions.length > 0 && (
                <>
                    <div className={styles.suggestionsContainer}>
                        {suggestionsData.suggestions.map((product, index) => (
                            <div
                                key={`${product.code}-${index}`}
                                className={`${styles.productCard} ${selectedProduct?.code === product.code ? styles.selected : ""}`}
                                onClick={() => handleSelectAndCreate(product)}
                            >
                                <div className={styles.productHeader}>
                                    <h3 className={styles.productCode}>Código: {product.code}</h3>
                                    <span className={styles.productModel}>Modelo: {product.modelo}</span>
                                </div>

                                <div className={styles.productDescription}>
                                    <p>{truncateText(product.descripcion)}</p>
                                </div>

                                <div className={styles.productFooter}>
                                    <small className={styles.documentName}>📄 {product.document_name}</small>
                                    {selectedProduct?.code === product.code && <span className={styles.selectedIndicator}>✓ Seleccionado</span>}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className={styles.actionsContainer}>
                        <button onClick={handleGoBack} className={styles.secondaryButton} disabled={isCreatingChat}>
                            Volver
                        </button>

                        {isCreatingChat ? (
                            <div className={styles.loadingButton}>
                                <ScaleLoader color="#ffffff" height={15} width={2} radius={2} margin={2} />
                                <span>Creando chat...</span>
                            </div>
                        ) : (
                            <button onClick={handleCreateChat} className={styles.primaryButton} disabled={!selectedProduct}>
                                Crear Chat con {selectedProduct?.code || "Producto Seleccionado"}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ChatByDescription;
